import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useOutletContext } from "react-router-dom"
import CreateCallModal from "../../CallsForArtist/CreateCallModal"
import EmptyState from "../../../components/Common/EmptyState"
import useInfinitePagination from "../../../hooks/useInfinitePagination"
import useCategories from "../../../hooks/useCategories"
import CityHubTabHeader from "../CityHubTabHeader"
import {
  CityHubInfiniteSentinel,
  OpportunityCard,
  OpportunityCardSkeleton,
} from "../CityHubCards"
import { CITY_HUB_INFINITE_PAGE_SIZE, PUBLIC_CITY_INFINITE_PAGE_SIZE, unwrapCityHubPagedResult } from "../cityHubTabs"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import {
  CALL_LIST_WITH,
  getCallCityId,
  mapApiCallToCityHubCard,
} from "../../CallsForArtist/callMappers"
import { goToRequestInvite } from "../../PublicCity/publicCityPaths"

const SKELETON_COUNT = 4
const LOAD_MORE_SKELETON_COUNT = 2

const CALL_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "published", label: "Published" },
  { value: "rejected", label: "Rejected" },
  { value: "closed", label: "Closed" },
]

const CALL_TYPE_OPTIONS = [
  { value: "formal", label: "Formal" },
  { value: "informal", label: "Informal" },
]

const normalizeSingleFilter = next =>
  Array.isArray(next) ? next[0] || "" : next || ""

const CityHubCallsTab = () => {
  const navigate = useNavigate()
  const currentUserId = useSelector(state => state.auth.currentUser?.id) || null
  const {
    tab,
    city,
    isCityLoading = false,
    isCityMember = false,
    isPublic = false,
    listScrollRootRef,
  } = useOutletContext()
  const cityId = city?.id || null
  const { categories } = useCategories()

  const defaultCallCityTypes = useMemo(() => {
    const type = String(city?.addressType || city?.tier || "")
      .trim()
      .toLowerCase()
    if (type === "primary" || type === "secondary" || type === "tertiary") {
      return [type]
    }
    return null
  }, [city?.addressType, city?.tier])

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [typeFilter, setTypeFilter] = useState("")
  const [categoryId, setCategoryId] = useState("")
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [calls, setCalls] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const sentinelRef = useRef(null)
  const pageRef = useRef(1)
  const loadInFlightRef = useRef(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const fetchCalls = useCallback(
    async ({ page: pageToLoad = 1, append = false } = {}) => {
      if (!cityId) {
        setCalls([])
        setHasMore(false)
        setError(null)
        setIsLoading(false)
        setIsLoadingMore(false)
        return
      }

      if (append) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
        setError(null)
      }

      try {
        const pageSize = isPublic
          ? PUBLIC_CITY_INFINITE_PAGE_SIZE
          : CITY_HUB_INFINITE_PAGE_SIZE
        const params = {
          page: pageToLoad,
          per_page: pageSize,
          page_size: pageSize,
          search: debouncedSearch || "",
          is_global: "",
          "with[]": CALL_LIST_WITH,
        }

        if (statusFilter) params.status = statusFilter
        if (typeFilter) params.type = typeFilter
        if (categoryId) params.category_id = categoryId

        const listUrl = isPublic
          ? endpoints.getPublicCityCalls(cityId)
          : endpoints.getCityCalls(cityId)
        const response = await get(listUrl, { params })

        if (!response?.data?.success) {
          const message = toastApiError(response, "Failed to load city calls")
          setError(message)
          if (!append) setCalls([])
          setHasMore(false)
          return
        }

        const { list, currentPage, hasMore: nextHasMore } =
          unwrapCityHubPagedResult(response, {
            pageToLoad,
            pageSize,
          })
        const mapped = list.map(mapApiCallToCityHubCard)

        setCalls(prev => (append ? [...prev, ...mapped] : mapped))
        pageRef.current = currentPage
        setHasMore(nextHasMore)
      } catch (err) {
        const message = toastApiError(err, "Failed to load city calls")
        setError(message)
        if (!append) setCalls([])
        setHasMore(false)
      } finally {
        loadInFlightRef.current = false
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [categoryId, cityId, debouncedSearch, isPublic, statusFilter, typeFilter],
  )

  useEffect(() => {
    pageRef.current = 1
    fetchCalls({ page: 1, append: false })
  }, [fetchCalls])

  const loadMore = useCallback(() => {
    if (
      !hasMore ||
      isLoading ||
      isLoadingMore ||
      loadInFlightRef.current
    ) {
      return
    }
    loadInFlightRef.current = true
    fetchCalls({ page: pageRef.current + 1, append: true })
  }, [fetchCalls, hasMore, isLoading, isLoadingMore])

  useInfinitePagination({
    hasMore: hasMore && !isLoading,
    isLoading: isLoadingMore,
    onLoadMore: loadMore,
    resetKey: calls.length,
    sentinelRef,
    rootRef: listScrollRootRef,
  })

  const filterConfig = useMemo(
    () => [
      {
        key: "status",
        type: "select",
        label: "Status",
        multiple: false,
        options: CALL_STATUS_OPTIONS,
        value: statusFilter,
        onChange: next => {
          setStatusFilter(normalizeSingleFilter(next))
        },
      },
      {
        key: "type",
        type: "select",
        label: "Type",
        multiple: false,
        options: CALL_TYPE_OPTIONS,
        value: typeFilter,
        onChange: next => {
          setTypeFilter(normalizeSingleFilter(next))
        },
      },
      {
        key: "category_id",
        type: "select",
        label: "Category",
        multiple: false,
        searchable: true,
        options: categories,
        value: categoryId,
        onChange: next => {
          setCategoryId(normalizeSingleFilter(next))
        },
      },
    ],
    [categories, categoryId, statusFilter, typeFilter],
  )

  const showInitialSkeleton = isCityLoading || isLoading
  const canCreateCall = Boolean(cityId && isCityMember && !isPublic)
  const showPublicCta = Boolean(isPublic && cityId)
  const headerCtaLabel = showPublicCta
    ? tab.ctaLabel
    : canCreateCall
      ? tab.ctaLabel
      : null

  const openInvite = useCallback(() => {
    goToRequestInvite(
      navigate,
      { from: "public-city", cityId, tab: "calls-for-artists", city },
      { isAuthenticated: Boolean(currentUserId) },
    )
  }, [city, cityId, currentUserId, navigate])

  return (
    <>
      <CityHubTabHeader
        title={tab.pageTitle}
        subtitle={tab.pageSubtitle}
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        searchPlaceholder={tab.searchPlaceholder}
        filterConfig={filterConfig}
        showFilter={tab.showFilter}
        ctaLabel={headerCtaLabel}
        onCtaClick={() => {
          if (showPublicCta) {
            openInvite()
            return
          }
          if (!canCreateCall) return
          setIsCreateOpen(true)
        }}
      />

      <div
        className="city-hub__calls-grid"
        aria-busy={showInitialSkeleton || isLoadingMore || undefined}
      >
        {showInitialSkeleton
          ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <OpportunityCardSkeleton key={`city-call-skeleton-${index}`} />
            ))
          : null}

        {!showInitialSkeleton &&
          calls.map(call => (
            <OpportunityCard
              key={call.id}
              item={call}
              onOpen={() => {
                if (isPublic) {
                  openInvite()
                  return
                }
                const resolvedCityId = cityId || getCallCityId(call)
                if (!call?.id || !resolvedCityId) return
                navigate(`/city-hub/calls/${call.id}`, {
                  state: {
                    cityId: cityId || resolvedCityId,
                    city,
                  },
                })
              }}
            />
          ))}

        {!showInitialSkeleton && isLoadingMore
          ? Array.from({ length: LOAD_MORE_SKELETON_COUNT }).map((_, index) => (
              <OpportunityCardSkeleton
                key={`city-call-more-skeleton-${index}`}
              />
            ))
          : null}

        {!showInitialSkeleton && !isLoadingMore && calls.length === 0 ? (
          <EmptyState
            title={error ? "Unable to load calls" : "No calls found"}
            description={
              error ||
              (!cityId
                ? "Select a city in the banner to browse calls."
                : isPublic
                  ? "Nothing matches this search or filter yet. Request an invite to join this city."
                  : !isCityMember
                    ? "Only members of this city can create calls. You can still browse as a visitor."
                    : "Nothing matches this search or filter yet.")
            }
            actionText={
              showPublicCta
                ? tab.ctaLabel
                : canCreateCall
                  ? "Create Call"
                  : undefined
            }
            onActionClick={
              showPublicCta
                ? openInvite
                : canCreateCall
                  ? () => setIsCreateOpen(true)
                  : undefined
            }
          />
        ) : null}
      </div>

      <CityHubInfiniteSentinel
        sentinelRef={sentinelRef}
        hasMore={hasMore && !isLoading}
      />

      {!isPublic ? (
        <CreateCallModal
          isOpen={isCreateOpen && canCreateCall}
          onClose={() => setIsCreateOpen(false)}
          defaultCityTypes={defaultCallCityTypes}
          allowedCityTypes={defaultCallCityTypes}
          onSubmit={async () => {
            await fetchCalls({ page: 1, append: false })
          }}
        />
      ) : null}
    </>
  )
}

export default CityHubCallsTab
